import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Button, Modal, DialogCancel } from "@cntrl/ui";
import styles from "./AvatarCropDialog.module.scss";

/** The square the server receives — big enough for every avatar surface (the largest render is the
 * 300px modal hero at 2x), small enough that a 17 MB phone photo leaves as ~100 KB. */
const OUT_SIZE = 512;
/** The on-screen crop viewport (px). */
const VIEW = 280;
const MAX_ZOOM = 3;

/**
 * The square-enforcement step between "picked a file" and "uploaded an avatar" (PO, 2026-08-20 — and
 * what makes big originals work at all: a 17 MB 6156×3462 JPEG died on the 5 MB cap; its CROP leaves
 * here as a ~100 KB 512² JPEG). Pan by dragging, zoom by slider; the export draws exactly the viewport
 * square through a canvas, so the server keeps its strict caps for API callers while the UI accepts
 * anything the browser can decode. A file the browser CANNOT decode (an HEIC renamed .jpg) gets the
 * designed error, named for what it is.
 */
export function AvatarCropDialog({
  file,
  onCancel,
  onCropped,
}: {
  file: File;
  onCancel: () => void;
  /** Receives the cropped square as a JPEG blob; the caller owns the upload. */
  onCropped: (blob: Blob) => void;
}) {
  const [src, setSrc] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [dims, setDims] = useState<{ w: number; h: number }>();
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [exporting, setExporting] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Cover scale: the image always fills the square; zoom multiplies it.
  const cover = dims ? VIEW / Math.min(dims.w, dims.h) : 1;
  const scale = cover * zoom;

  const clamp = (o: { x: number; y: number }, s: number) => {
    if (!dims) return o;
    const maxX = Math.max(0, (dims.w * s - VIEW) / 2);
    const maxY = Math.max(0, (dims.h * s - VIEW) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, o.x)), y: Math.min(maxY, Math.max(-maxY, o.y)) };
  };

  const onPointerDown = (e: ReactPointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drag.current) return;
    setOffset(clamp({ x: drag.current.ox + (e.clientX - drag.current.x), y: drag.current.oy + (e.clientY - drag.current.y) }, scale));
  };
  const onPointerUp = () => (drag.current = null);

  const onZoom = (z: number) => {
    setZoom(z);
    setOffset((o) => clamp(o, cover * z));
  };

  const confirm = () => {
    const img = imgRef.current;
    if (!img || !dims) return;
    setExporting(true);
    const canvas = document.createElement("canvas");
    canvas.width = OUT_SIZE;
    canvas.height = OUT_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setExporting(false);
      return;
    }
    // The viewport square, mapped back into source pixels: centre = image centre minus the pan,
    // half-side = half the viewport at the current scale.
    const srcSize = VIEW / scale;
    const srcX = dims.w / 2 - offset.x / scale - srcSize / 2;
    const srcY = dims.h / 2 - offset.y / scale - srcSize / 2;
    ctx.drawImage(img, srcX, srcY, srcSize, srcSize, 0, 0, OUT_SIZE, OUT_SIZE);
    canvas.toBlob(
      (blob) => {
        setExporting(false);
        if (blob) onCropped(blob);
      },
      "image/jpeg",
      0.9,
    );
  };

  return (
    // Headerless (PO, 2026-08-20): the crop viewport says what this is; the title survives as the
    // sr-only accessible name, and Cancel/Escape/scrim all dismiss.
    <Modal title="Crop your photo" onClose={onCancel} closeLabel="Cancel crop" size="sm" hideHeader>
      <div className={styles.body}>
        {failed ? (
          <p className={styles.failed} role="alert">
            That file can't be read as an image — it may not really be a JPEG/PNG/WebP inside. Try
            exporting it again from your photos app.
          </p>
        ) : (
          <>
            <div
              className={styles.viewport}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              {src && (
                <img
                  ref={imgRef}
                  src={src}
                  alt="Crop preview — drag to reposition"
                  draggable={false}
                  className={styles.image}
                  style={
                    dims
                      ? {
                          width: dims.w * scale,
                          height: dims.h * scale,
                          transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
                        }
                      : undefined
                  }
                  onLoad={(e) => setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                  onError={() => setFailed(true)}
                />
              )}
            </div>
            <label className={styles.zoomRow}>
              <span className={styles.zoomLabel}>Zoom</span>
              <input
                type="range"
                min={1}
                max={MAX_ZOOM}
                step={0.01}
                value={zoom}
                onChange={(e) => onZoom(Number(e.target.value))}
              />
            </label>
          </>
        )}

        <footer className={styles.footer}>
          <DialogCancel />
          {!failed && (
            <Button onClick={confirm} loading={exporting} disabled={!dims}>
              Use photo
            </Button>
          )}
        </footer>
      </div>
    </Modal>
  );
}
